// akteSofortSpeichern.ts — was die Karte „Verantwortung fürs Unternehmen" von
// der Akte braucht, um unternehmensbezogene Daten SOFORT zu speichern (User
// 30.09.2026): Inline-Editing in „Mensch im Unternehmen" und „Daten ändern" im
// Popup, auch bei geschlossenem Schloss.
//
// Das Sofort-Speichern umgeht das Modell „Schloss zu = speichern". Deshalb:
//   - nur mit Schreibrecht an der Akte (`darfSchreiben`),
//   - nie, solange das Formular ungespeicherte Änderungen hat
//     (`hatUngespeicherte`) — das Neuladen danach würfe sie weg,
//   - danach immer neu laden (`neuLaden`), damit Formular und Ladestand wieder
//     zum Server passen.
//
// Die Anlage (neue Firma) stellt den Kontext nicht bereit — dort bleibt alles
// beim Sammeln im Formular.

import * as React from "react";

export type AkteSofortSpeichern = {
  darfSchreiben: boolean;
  /**
   * Darf der Benutzer Kontakte löschen (admin/gfgl oder temporäre
   * Lösch-Allowlist)? Dann bieten die Beziehungslisten neben „Entfernen" (X)
   * auch „Kontakt löschen" (Eimer) an (Plan 2026-10-01, Paket A2).
   */
  darfKontakteLoeschen?: boolean;
  hatUngespeicherte: () => boolean;
  neuLaden: () => void;
  /**
   * Kleiner Zwischenspeicher für die Ansicht der Karte (aufgeklappt, welche
   * Zeile gerade bearbeitet wird). Das Neuladen baut die Maske neu auf — ohne
   * ihn klappte „Mensch im Unternehmen" nach jedem gespeicherten Feld wieder zu.
   */
  ansicht: React.MutableRefObject<Record<string, unknown>>;
};

export const AkteSofortSpeichernContext = React.createContext<AkteSofortSpeichern | null>(null);

export const useAkteSofortSpeichern = (): AkteSofortSpeichern | null => React.useContext(AkteSofortSpeichernContext);

/** Hinweis, wenn wegen offener Formularänderungen nicht sofort gespeichert wird. */
export const UNGESPEICHERT_HINWEIS =
  "In der Akte gibt es noch ungespeicherte Änderungen. Bitte zuerst speichern (Schloss schließen) oder verwerfen — danach lässt sich das hier direkt ändern.";

/** Ansichtszustand, der ein Neuladen der Maske überlebt (s. `ansicht`). */
export function useAnsichtsZustand<T>(
  ctx: AkteSofortSpeichern | null,
  schluessel: string,
  start: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [wert, setWert] = React.useState<T>(() =>
    ctx && schluessel in ctx.ansicht.current ? (ctx.ansicht.current[schluessel] as T) : start,
  );
  React.useEffect(() => {
    if (ctx) ctx.ansicht.current[schluessel] = wert;
  }, [ctx, schluessel, wert]);
  return [wert, setWert];
}
